import { motion } from "framer-motion";
import { Clock, FileStack, PhoneOff, LineChart, ArrowRight } from "lucide-react";

/* Hidden cost of disconnected systems — pivot band between hero and Meet OS.
   Four uniform stat cards with a hard number, a bold title, and a body. */
const COST_ITEMS = [
  {
    stat: "4 - 6",
    statUnit: "hrs",
    label: "Time",
    title: "Hours lost every week.",
    desc: "Staff switching between platforms, searching for patient info, and re-keying the same data into 5 places.",
    solvedBy: "Clinic Hub",
    Icon: Clock,
    accent: "violet",
  },
  {
    stat: "30",
    statUnit: "%",
    label: "Admin",
    title: "Extra admin work created.",
    desc: "Duplicate entry, missed updates and broken workflows pile on overhead that helps no patient.",
    solvedBy: "Automations",
    Icon: FileStack,
    accent: "blue",
  },
  {
    stat: "27",
    statUnit: "%",
    label: "Revenue",
    title: "Inbound calls unanswered.",
    desc: "Forgotten follow-ups and missed after-hours calls leak bookings every day — without you ever seeing it.",
    solvedBy: "AI Workforce",
    Icon: PhoneOff,
    accent: "rose",
  },
  {
    stat: "5+",
    statUnit: "hrs",
    label: "Insight",
    title: "Weekly spreadsheet work.",
    desc: "Owners burn hours each week rebuilding reports across spreadsheets just to see what's working.",
    solvedBy: "Clinic Pulse",
    Icon: LineChart,
    accent: "amber",
  },
];

const ACCENT = {
  violet:  { chip: "bg-violet-500/20 text-violet-200 ring-violet-400/30", dot: "bg-violet-400", unit: "text-violet-300", iconBg: "bg-violet-500/15 text-violet-300", glow: "from-violet-500/35", bar: "from-violet-500 via-violet-400 to-violet-500", numberGrad: "from-white via-white to-violet-200/60", solvedDot: "bg-violet-400" },
  blue:    { chip: "bg-blue-500/20 text-blue-200 ring-blue-400/30",       dot: "bg-blue-400",   unit: "text-blue-300",   iconBg: "bg-blue-500/15 text-blue-300",     glow: "from-blue-500/30",   bar: "from-blue-500 via-blue-400 to-blue-500",       numberGrad: "from-white via-white to-blue-200/60",   solvedDot: "bg-blue-400" },
  rose:    { chip: "bg-rose-500/20 text-rose-200 ring-rose-400/30",       dot: "bg-rose-400",   unit: "text-rose-300",   iconBg: "bg-rose-500/15 text-rose-300",     glow: "from-rose-500/35",   bar: "from-rose-500 via-rose-400 to-rose-500",       numberGrad: "from-white via-white to-rose-200/60",   solvedDot: "bg-rose-400" },
  amber:   { chip: "bg-amber-500/20 text-amber-200 ring-amber-400/30",    dot: "bg-amber-400",  unit: "text-amber-300",  iconBg: "bg-amber-500/15 text-amber-300",   glow: "from-amber-500/30",  bar: "from-amber-500 via-amber-400 to-amber-500",    numberGrad: "from-white via-white to-amber-200/60",  solvedDot: "bg-amber-400" },
};

export const DisconnectedCost = () => (
  <section
    id="disconnected-cost"
    data-testid="disconnected-cost-section"
    className="relative overflow-hidden bg-gradient-to-b from-transparent via-rose-50/35 to-amber-50/40 py-16 dark:from-transparent dark:via-transparent dark:to-transparent sm:py-24"
  >
    <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px]">
      <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(60%_60%_at_50%_0%,rgba(244,63,94,0.10),transparent_70%)]" />
    </div>

    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl"
      >
        <p className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
          The hidden cost
        </p>
        <h2 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          The hidden cost of{" "}
          <span className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 bg-clip-text text-transparent">
            disconnected systems.
          </span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          You&apos;re not just paying for 12 logins. Every disconnected tool quietly
          steals your team&apos;s time, your revenue and your visibility.
        </p>
      </motion.div>

      {/* 4 uniform stat cards — dark, high-contrast */}
      <div className="mt-12 grid grid-cols-2 gap-3 sm:mt-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
        {COST_ITEMS.map((c, i) => {
          const tone = ACCENT[c.accent];
          const Icon = c.Icon;
          return (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              data-testid={`cost-card-${i}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-950 p-4 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] ring-1 ring-inset ring-white/[0.04] transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(15,23,42,0.45)] sm:p-5"
            >
              {/* decorative accent corner glow */}
              <div className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br ${tone.glow} to-transparent blur-3xl`} />
              {/* subtle grid texture */}
              <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />

              {/* Header row: chip label + icon */}
              <div className="relative flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-display text-[11.5px] font-black uppercase tracking-[0.22em] ring-1 ring-inset ${tone.chip}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                  {c.label}
                </span>
                <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tone.iconBg} ring-1 ring-inset ring-white/[0.06]`}>
                  <Icon className="h-[15px] w-[15px]" />
                </span>
              </div>

              {/* Big stat with uniformly-styled accent unit */}
              <p className="relative mt-4 flex items-baseline gap-1 font-display leading-none sm:mt-5">
                <span className={`bg-gradient-to-b ${tone.numberGrad} bg-clip-text text-[2.25rem] font-black tracking-[-0.04em] text-transparent sm:text-[3rem] lg:text-[3.5rem]`}>{c.stat}</span>
                {c.statUnit && (
                  <span className={`text-[1.1rem] font-black tracking-tight sm:text-[1.5rem] lg:text-[1.75rem] ${tone.unit}`}>
                    {c.statUnit}
                  </span>
                )}
              </p>

              {/* Title */}
              <p className="relative mt-3 font-display text-[14px] font-bold leading-snug tracking-[0.01em] text-white sm:mt-4 sm:text-[15px] lg:text-[16px]">
                {c.title}
              </p>

              {/* Description — readable muted white */}
              <p className="relative mt-1.5 text-[12px] leading-relaxed text-white/70 sm:text-[12.5px]">
                {c.desc}
              </p>

              {/* Solved by — narrative payoff */}
              <div className="relative mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/10 pt-2.5 sm:mt-4 sm:pt-3">
                <span className={`h-1.5 w-1.5 rounded-full ${tone.solvedDot}`} />
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45 sm:text-[10.5px] sm:tracking-[0.16em]">Solved by</span>
                <span className="text-[11.5px] font-bold tracking-tight text-white sm:text-[12px]">{c.solvedBy}</span>
                <ArrowRight className="h-3 w-3 text-white/40 transition-all group-hover:translate-x-0.5 group-hover:text-white/70" />
              </div>

              {/* Bottom accent bar */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute inset-x-0 bottom-0 h-[3px] origin-left bg-gradient-to-r ${tone.bar}`}
              />
            </motion.div>
          );
        })}
      </div>

      {/* Pivot bottom line — quick scroll to solution */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.25 }}
        className="mt-12 flex flex-col items-center gap-3 text-center"
      >
        <a
          href="#meet-clinic-os"
          data-testid="disconnected-cost-better-way"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("meet-clinic-os")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="group inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-5 py-2 font-display text-sm font-bold uppercase tracking-[0.22em] text-muted-foreground shadow-sm backdrop-blur transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:bg-card hover:text-foreground hover:shadow-md"
        >
          <span className="animate-bounce text-base">↓</span>
          There&apos;s a better way
        </a>
      </motion.div>
    </div>
  </section>
);
